<template>
  <div class="kpt-detail-content-page" v-if="device">
    <vue-headful :title="`${device.serial} - Overview`" />
    <div class="kpt-detail-content-actions">
      <div class="buttons">
        <div v-if="!isTransferVisible">
          <b-button type="is-primary is-outlined" size="is-small" icon-left="sync-alt" :disabled="isRefreshing" @click="refreshDevice">Refresh</b-button>
          <b-tooltip :active="!!transferDeviceValidationError" 
            type="is-warning" 
            :label="transferDeviceValidationError"
            position="is-right">
            <b-button type="is-primary is-outlined" icon-left="exchange" size="is-small" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')" :disabled="isRefreshing || !!transferDeviceValidationError || isCancellingPendingTransfer" @click="transferDeviceEdit">Transfer</b-button>
          </b-tooltip>

        </div>
        <div v-if="isTransferVisible">
          <b-button type="is-primary is-outlined" size="is-small" icon-left="exchange" :loading="isTransferring" :disabled="!isTransferPermitted" @click="transferDevice">Transfer</b-button>
          <b-button type="is-primary is-outlined" size="is-small" icon-left="times" @click="cancelTransferEdit">Cancel</b-button>
        </div>
      </div>
      <div class="kpt-transfer-device-action" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin') && isTransferVisible">
        <b-message type="is-warning" title="Please Note" class="mb-2">
          Transferring a Device to another Account relinquishes ownership of the Device. This operation cannot be undone unless the target Account chooses to transfer the Device back to your Account.
        </b-message>
        <b-field label="Account">
          <b-select placeholder="Select an account" v-model="clonedDevice.accountId" :disabled="isAccountSelectDisabled" @input="validateInputs">
            <option
              v-for="option in allAccounts"
              :value="option.id"
              :key="option.id">
              {{ option.name }}
            </option>
          </b-select>
        </b-field>
        <b-message type="is-info" class="my-4">
          <strong>OR</strong> enter an email address and we will attempt to match the address with an existing Account. If no match is found, the recipient will be invited to establish an account with the Kymeta Access Portal via email.
        </b-message>
        <b-field label="Email Address" 
          :type="{'is-danger' : validationErrors.includes('transferToEmailAddress')}" 
          :message="{'This email is invalid.' : validationErrors.includes('transferToEmailAddress')}">
          <b-input 
            type="email" 
            placeholder="someone@kymetacorp.com" 
            v-model="transferToEmailAddress" 
            @input="emailAddressInputReceived"></b-input>
        </b-field>
      </div>
    </div>
    <header>
      <h6 class="is-uppercase"><strong>Details</strong></h6>
    </header>
    <b-message type="is-info" class="my-4" :active="isPendingTransfer" style="position: relative;">
      <p>Device is pending a transfer to '{{ device.pendingTransferEmail }}'</p>
      <div v-if="device.remoteId">
        <span class="has-text-danger is-italic">To cancel this pending transfer please initiate the cancel from the associated Remote.</span>
      </div>
      <div v-else>
        <a class="has-text-danger" @click="cancelPendingTransfer">Cancel Pending Transfer</a>
      </div>
      <b-loading :is-full-page="false" v-model="isCancellingPendingTransfer" :can-cancel="false"></b-loading>
    </b-message>
    <detail-field>
      <template v-slot:label>Account</template>
      <template v-slot:value>{{device.accountName || '-'}}</template>
    </detail-field>
    <detail-field>
      <template v-slot:label>Remote</template>
      <template v-slot:value>
        <router-link v-if="device.remoteId" :to="{ name: 'remoteOverview', params: { id: device.remoteId }}">{{device.remoteName}}</router-link>
        <span v-else>-</span>
      </template>
    </detail-field>
    <detail-field>
      <template v-slot:label>Last Checkin</template>
      <template v-slot:value>{{device.lastCheckin ? format(new Date(device.lastCheckin), 'Pp') : '-'}}</template>
    </detail-field>
    <detail-field>
      <template v-slot:label>Type</template>
      <template v-slot:value>{{device.deviceType}}</template>
    </detail-field>
    <detail-field>
      <template v-slot:label>Model</template>
      <template v-slot:value>{{device.deviceModel}}</template>
    </detail-field>
    <detail-field>
      <template v-slot:label>Part Number</template>
      <template v-slot:value>{{device.partNumber}}</template>
    </detail-field>
    <hr>
    <header>
      <h6 class="is-uppercase"><strong>Software</strong></h6>
    </header>
    <p v-if="!device.software">No software information available.</p>
    <div v-else>
      <detail-field v-if="device.software">
        <template v-slot:label>Current Version</template>
        <template v-slot:value>{{device.software.currentVersion || 'Unknown'}}</template>
      </detail-field>
      <detail-field v-if="device.software">
        <template v-slot:label>Locked</template>
        <template v-slot:value>{{device.software.isLocked ? 'Yes' : 'No'}}</template>
      </detail-field>
    </div>
    <hr>
    <header>
      <h6 class="is-uppercase"><strong>Antenna Features</strong></h6>
    </header>
    <p v-if="!device.features || !device.features.length">No features enabled for this antenna.</p>
    <b-taglist class="kpt-detail-features" v-else>
      <b-tag type="is-success" v-for="(feature, idx) in device.features" :key="idx">{{ getFeatureDisplayName(feature) }}</b-tag>
    </b-taglist>
  </div>
</template>
<script>
import { orderBy, uniqBy } from 'lodash';
import { format } from 'date-fns';
import { idToNameParserMixin } from '../../shared/mixins/idToNameParser';

export default {
  data() {
    return {
      format,
      isRefreshing: false,
      isTransferVisible: false,
      isTransferring: false,
      transferToEmailAddress: null,
      validationErrors: [],
      isTransferPermitted: false,
      isCancellingPendingTransfer: false,
      isAccountSelectDisabled: false,
      clonedDevice: null
    }
  },
  mixins: [idToNameParserMixin],
  computed: {
    device() {
      return this.$store.state.devices.device;
    },
    featureTypes() {
      return this.$store.state.devices.features;
    },
    accounts() { 
      return orderBy(this.$store.state.accounts.accounts, ['name'], ['asc']);
    },
    sharedAccounts() {
      return orderBy(this.$store.state.accounts.sharedAccounts, ['name'], ['asc']);
    },
    allAccounts() {
      // join accounts and sharedAccounts arrays
      const concatAccounts = this.accounts.concat(this.sharedAccounts);
      // return unique to prevent duplicates
      return uniqBy(concatAccounts, 'id');
    },
    isKymetaDevice() {
      return !this.device.accountId || (this.device.accountId.toLowerCase() == this.$store.state.appConfig.defaultAccountId);
    },
    isSharedDevice() {
      // flatten array to just account id's
      const hierarchyAccountIds = this.accounts.map(a => a.id);
      // check to see if the device account matches the users account hierarchy
      const hierarchyAccountFound = hierarchyAccountIds.includes(this.device.accountId);
      return !hierarchyAccountFound;
    },
    isPendingTransfer() {
      return !!this.device.pendingTransferEmail;
    },
    transferDeviceValidationError() {
      if (this.isSharedDevice) return 'Unable to transfer shared Devices.';
      if (this.isPendingTransfer) return 'Pending transfer has already been initiated.';
      if (this.device.remoteId) return 'Unable to transfer Devices associated with a Remote.';
      return null;
    },
    tableFilterParam() {
      return this.$store.state.devices.tableFilterParam;
    }
  },
  methods: {
    async refreshDevice() {
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'refreshDevice', 'Refresh device clicked.', { deviceId: this.device.id });
      await this.$store.dispatch('fetchDevice', this.device.id);
      return true;
    },
    transferDeviceEdit() {
      this.clonedDevice = JSON.parse(JSON.stringify(this.device));
      this.isTransferVisible = true;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'transferDeviceEdit', 'Transfer device clicked.', { deviceId: this.device.id });
    },
    async transferDevice() {
      const payload = {
        id: this.device.id,
        accountId: this.clonedDevice.accountId != this.device.accountId ? this.clonedDevice.accountId : null,
        emailAddress: this.transferToEmailAddress
      };

      const originalAccountId = this.device.accountId;
      this.isTransferring = true;
      const transferResult = await this.$store.dispatch('transferDevice', payload);
      this.isTransferring = false;
      if (!transferResult.isSuccess) {
        this.$buefy.toast.open({
          duration: 3000,
          message: transferResult.error || 'Error encountered while attempting to transfer.',
          type: 'is-danger'
        });
        return;
      }

      // reset the vars for the next transfer or edit
      this.clonedDevice = null;
      this.transferToEmailAddress = null;
      this.isAccountSelectDisabled = false;

      this.isTransferVisible = false;
      const { device: updatedDevice } = transferResult;
      // check to see if accountId changed
      if (updatedDevice.accountId != originalAccountId) {
        // check account hierarchy to see if user still has permission to the device (if they transferred to a child Account they retain access)
        const userAccounts = this.accounts.map(a => a.id);
        if (!userAccounts.includes(updatedDevice.accountId)) {
          // user no longer has access to the device, redirect user to list page & refresh list (ensure device is not present)
          this.$router.push({ path: '/devices' });
        }
      }
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'transferDevice', 'Device transferred.', { deviceId: this.device.id });
    },
    async cancelPendingTransfer() {
      this.isCancellingPendingTransfer = true;
      const cancelTransferResult = await this.$store.dispatch('cancelPendingDeviceTransfer', { id: this.device.id });
      this.isCancellingPendingTransfer = false;
      if (!cancelTransferResult.isSuccess) {
        this.$buefy.toast.open({
          duration: 3000,
          message: cancelTransferResult.error || 'Error encountered while attempting to cancel the pending transfer.',
          type: 'is-danger'
        });
        return;
      }

      // indicate success
      this.$buefy.toast.open({
        duration: 3000,
        message: 'Pending transfer cancelled.',
        type: 'is-success'
      });
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'cancelPendingDeviceTransfer', 'Pending device transfer cancelled.', { deviceId: this.device.id });
    },
    cancelTransferEdit() {
      this.clonedDevice = null;
      this.isTransferVisible = false;
      this.isTransferPermitted = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'cancelDeviceTransferEdit', 'Cancelled edit device transfer.', { deviceId: this.device.id });
    },
    testEmailAddressValidity(emailAddress) {
      const re = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      return re.test(emailAddress);
    },
    emailAddressInputReceived() {
      this.validateInputs();

      // reset account and disable dropdown
      if (this.transferToEmailAddress) {
        this.clonedDevice = JSON.parse(JSON.stringify(this.device));
        this.isAccountSelectDisabled = true;
      } else {
        this.isAccountSelectDisabled = false;
      }
    },
    validateInputs() {
      // reset default
      this.isTransferPermitted = false;
      this.validationErrors = [];

      // validate the email address when it is present
      if (this.transferToEmailAddress && !this.testEmailAddressValidity(this.transferToEmailAddress)) { 
        this.validationErrors.push('transferToEmailAddress');
        this.isTransferPermitted = false;
        return;
      }

      if (!this.transferToEmailAddress && this.clonedDevice.accountId == this.device.accountId) return;

      this.isTransferPermitted = true;
    },
    getFeatureDisplayName(feat) {
      const featureMatch = this.featureTypes.find(ft => ft.shortcode == feat);
      return featureMatch ? featureMatch.name : '-';
    }
  },
  mounted() {
    this.accountToTransferToId = this.device.accountId || null;
  }
}
</script>

<style lang="scss" scoped>
  .kpt-detail-features {
    margin-top: 5px;
  }
  .kpt-detail-content-actions{
    // align tooltip with action buttons
    ::v-deep .tooltip-content {
      margin-top: -5px;
    }
  }
</style>
